import React from 'react';
import { Slider, Box } from '@mui/material';
import ValueLabel from './ValueLabel';

function toNumber(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

function stepsCount(min, max, step) {
  return Math.floor((max - min) / step) + 1;
}

function computeDefault(min, max, step, provided) {
  const clamped = typeof provided === 'number' ? Math.min(max, Math.max(min, provided)) : null;
  if (clamped !== null) return clamped;
  const count = stepsCount(min, max, step);
  if (count % 2 === 1) {
    return min + ((count - 1) / 2) * step;
  }
  // even number of steps: start at min per spec
  return min;
}

function isObjMark(l) {
  return l && typeof l === 'object' && 'value' in l && 'label' in l;
}

function buildMarks(min, max, step, labels) {
  if (!Array.isArray(labels) || labels.length === 0) return [];

  // Case 1: labels provided as array of objects: [{ value:number, label:string }]
  if (labels.some(isObjMark)) {
    return labels
      .filter(isObjMark)
      .map(l => ({ value: toNumber(l.value), label: (l.label ?? '').toString() }))
      .filter(m => m.value !== null && m.value >= min && m.value <= max);
  }

  // Case 2: primitive labels aligned to steps
  const total = stepsCount(min, max, step);
  if (labels.length === total) {
    return labels.map((label, idx) => ({ value: min + idx * step, label: (label ?? '').toString() }));
  }
  const first = (labels.find(l => (l ?? '').toString().trim() !== '') ?? labels[0]) || '';
  const last = (labels.slice().reverse().find(l => (l ?? '').toString().trim() !== '') ?? labels[labels.length - 1]) || '';
  return [
    { value: min, label: first },
    { value: max, label: last },
  ];
}

export default function NumericSlider({ question, value, onChange }) {
  const hasRange = Array.isArray(question?.range);
  const min = hasRange ? (toNumber(question.range[0]) ?? 0) : (toNumber(question?.min) ?? 0);
  const max = hasRange ? (toNumber(question.range[1]) ?? 100) : (toNumber(question?.max) ?? 100);
  const step = typeof question?.step === 'number' ? question.step : 1;
  const labels = Array.isArray(question?.labels) ? question.labels : [];
  const defaultValue = computeDefault(min, max, step, toNumber(question?.defaultValue));
  const numericValue = typeof value === 'number' ? value : (toNumber(value) ?? defaultValue);
  const marks = buildMarks(min, max, step, labels);

  return (
    <Box sx={{ px: 2 }}>
      <Slider
        value={numericValue}
        min={min}
        max={max}
        step={step}
        marks={marks}
        onChange={(_, v) => {
          const n = Array.isArray(v) ? v[0] : v;
          onChange?.(typeof n === 'number' ? n : numericValue);
        }}
        valueLabelDisplay="auto"
        components={{ ValueLabel }}
        valueLabelFormat={(v) => {
          // Prefer object label match by value when provided
          if (labels.some(isObjMark)) {
            const match = labels.find(l => isObjMark(l) && toNumber(l.value) === v);
            const lbl = match?.label;
            if (lbl !== undefined && (lbl ?? '').toString().trim() !== '') return lbl;
          }
          // Fallback to index-based primitive labels
          const idx = Math.round((v - min) / step);
          const lbl = labels[idx];
          return (lbl !== undefined && (lbl ?? '').toString().trim() !== '') ? lbl : v;
        }}
      />
    </Box>
  );
}
